import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, CreditCard, ExternalLink, Loader2, Receipt, Users } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import CancellationModal from "@/components/subscription/CancellationModal";
import { useAuth } from "@/hooks/useAuth";
import { useHousehold } from "@/hooks/useHousehold";
import { useLearnerSeats } from "@/hooks/useLearnerSeats";
import { buildBillingSummary } from "@/lib/billing/billingSummary";
import { evaluatePlanChange } from "@/lib/billing/planChange";
import { trackSeatEvent } from "@/lib/telemetry/seatTelemetry";

const PLAN_OPTIONS = [
  { key: "individual_monthly", label: "Individual · Monthly", price: "$24.99/mo", max: 1 },
  { key: "individual_annual", label: "Individual · Annual", price: "$249/yr", max: 1 },
  { key: "family_monthly", label: "Family · Monthly", price: "$39.99/mo", max: 4 },
  { key: "family_annual", label: "Family · Annual", price: "$399/yr", max: 4 },
] as const;

const money = (cents: number, currency: string) =>
  new Intl.NumberFormat(undefined, { style: "currency", currency: currency.toUpperCase() }).format(cents / 100);

export default function AccountBilling() {
  const { user } = useAuth();
  const hh = useHousehold() as ReturnType<typeof useHousehold> & {
    changePlan?: (planKey: string) => Promise<unknown>;
  };
  const { household, subscription, loading } = hh;
  const isOwner = !!user && !!household && household.owner_id === user.id;
  const seatsQuery = useLearnerSeats(isOwner ? household?.id : null);
  const seats = seatsQuery.data?.summary ?? { max: 0, used: 0, available: 0, full: false, unassigned: 0 };
  const [showCancel, setShowCancel] = useState(false);
  const [busy, setBusy] = useState<string | null>(null);
  const [planMessage, setPlanMessage] = useState<string | null>(null);

  useEffect(() => {
    if (isOwner) trackSeatEvent("billing_settings_viewed", { surface: "account_settings" });
  }, [isOwner]);

  if (loading) {
    return (
      <main className="flex min-h-screen items-center justify-center" aria-busy="true">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" aria-hidden="true" />
        <span className="sr-only">Loading billing</span>
      </main>
    );
  }

  const header = (
    <header className="mb-6 space-y-3">
      <Link to="/profile" className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground">
        <ArrowLeft className="h-4 w-4" aria-hidden="true" /> Back to account
      </Link>
      <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Account Settings</p>
      <h1 className="font-heading text-2xl font-bold text-foreground">Billing &amp; Subscription</h1>
    </header>
  );

  if (!isOwner) {
    return (
      <main className="theme-auth mx-auto min-h-screen max-w-2xl px-4 py-8">
        {header}
        <section className="rounded-2xl border border-border bg-card p-6" role="status">
          <p className="text-sm text-foreground">Only the account owner can manage billing.</p>
          <p className="mt-2 text-sm text-muted-foreground">Ask the person who purchased HeirWise for your household to make billing changes.</p>
        </section>
      </main>
    );
  }

  const summary = buildBillingSummary(subscription, seats);
  const run = async (key: string, fn: () => Promise<unknown>, okMsg?: string) => {
    if (busy) return;
    setBusy(key);
    try {
      await fn();
      if (okMsg) toast.success(okMsg);
      trackSeatEvent(`billing_${key}`, { outcome: "success" });
    } catch (e) {
      trackSeatEvent(`billing_${key}`, { outcome: "error" });
      toast.error(e instanceof Error ? e.message : "Billing request failed.");
    } finally {
      setBusy(null);
    }
  };

  const requestPlanChange = async (planKey: string, targetMax: number) => {
    const decision = evaluatePlanChange({ currentMax: seats.max, targetMax, used: seats.used });
    if (!decision.allowed) {
      setPlanMessage(
        `This plan includes ${targetMax} learner seat${targetMax === 1 ? "" : "s"}. Free ${decision.seatsToFree} seat${decision.seatsToFree === 1 ? "" : "s"} from your household first. No one is removed automatically and learning history is kept.`,
      );
      trackSeatEvent("billing_plan_change_blocked", { reason_code: "seat_resolution_required" });
      return;
    }
    setPlanMessage(null);
    if (!hh.changePlan) return;
    await run("plan_change", () => hh.changePlan!(planKey), "Plan updated.");
  };

  const hasSub = !!subscription.plan_name && subscription.status !== "inactive";

  return (
    <main className="theme-auth mx-auto min-h-screen max-w-2xl px-4 py-8">
      {header}

      <section aria-labelledby="plan-heading" className="mb-4 rounded-2xl border border-border bg-card p-5">
        <h2 id="plan-heading" className="mb-3 text-sm font-semibold text-muted-foreground">Current plan</h2>
        <dl className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <div><dt className="text-xs text-muted-foreground">Plan</dt><dd className="font-semibold text-foreground">{summary.planLabel}</dd></div>
          {summary.cadence && <div><dt className="text-xs text-muted-foreground">Billing</dt><dd className="text-foreground">{summary.cadence}</dd></div>}
          <div><dt className="text-xs text-muted-foreground">Status</dt><dd className="text-foreground">{summary.statusLabel}</dd></div>
          {summary.date && <div><dt className="text-xs text-muted-foreground">{summary.dateLabel}</dt><dd className="text-foreground">{summary.date}</dd></div>}
        </dl>
        <p className="mt-4 flex items-center gap-2 text-sm text-foreground"><Users className="h-4 w-4" aria-hidden="true" />{summary.seatsLabel}</p>
        {subscription.cancel_at_period_end && summary.date && (
          <p className="mt-3 rounded-lg border border-border bg-muted/40 p-3 text-sm text-muted-foreground">
            Your subscription is set to end. Learners keep access until {summary.date}; progress is saved.
          </p>
        )}
      </section>

      <section aria-labelledby="payment-heading" className="mb-4 rounded-2xl border border-border bg-card p-5">
        <h2 id="payment-heading" className="mb-3 text-sm font-semibold text-muted-foreground">Payment</h2>
        <p className="flex items-center gap-2 text-sm text-foreground">
          <CreditCard className="h-4 w-4" aria-hidden="true" />
          {summary.paymentLabel ?? "No card on file"}
        </p>
        <div className="mt-4 flex flex-col gap-3 sm:flex-row">
          <Button onClick={() => run("portal", hh.openBillingPortal)} disabled={busy === "portal"} aria-busy={busy === "portal"}>
            {busy === "portal" && <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />}
            Manage payment &amp; invoices
          </Button>
          {hasSub && (subscription.cancel_at_period_end ? (
            <Button variant="outline" onClick={() => run("reactivate", hh.reactivateSubscription, "Subscription reactivated.")} disabled={busy === "reactivate"} aria-busy={busy === "reactivate"}>
              Reactivate subscription
            </Button>
          ) : (
            <Button variant="outline" onClick={() => setShowCancel(true)}>Cancel subscription</Button>
          ))}
        </div>
        <p className="mt-2 text-xs text-muted-foreground">Payment details are handled securely by Stripe. HeirWise never stores your card number.</p>
      </section>

      {hasSub && (
        <section aria-labelledby="change-heading" className="mb-4 rounded-2xl border border-border bg-card p-5">
          <h2 id="change-heading" className="mb-3 text-sm font-semibold text-muted-foreground">Change plan</h2>
          <ul className="space-y-2">
            {PLAN_OPTIONS.filter((p) => p.label.replace(" · ", " ").toLowerCase() !== summary.planLabel.replace("HeirWise ", "").toLowerCase()).map((p) => (
              <li key={p.key} className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-border p-3">
                <span className="text-sm text-foreground">{p.label} <span className="text-muted-foreground">· {p.price} · {p.max} seat{p.max === 1 ? "" : "s"}</span></span>
                <Button size="sm" variant="outline" disabled={!!busy} onClick={() => requestPlanChange(p.key, p.max)} aria-label={`Switch to ${p.label}`}>Switch</Button>
              </li>
            ))}
          </ul>
          {planMessage && <p role="alert" className="mt-3 text-sm text-destructive">{planMessage}</p>}
        </section>
      )}

      <section aria-labelledby="invoices-heading" className="rounded-2xl border border-border bg-card p-5">
        <h2 id="invoices-heading" className="mb-3 flex items-center gap-2 text-sm font-semibold text-muted-foreground"><Receipt className="h-4 w-4" aria-hidden="true" />Recent invoices</h2>
        {subscription.invoices.length === 0 ? (
          <p className="text-sm text-muted-foreground">No invoices yet.</p>
        ) : (
          <ul className="divide-y divide-border">
            {subscription.invoices.map((inv) => (
              <li key={inv.id} className="flex flex-wrap items-center justify-between gap-2 py-2 text-sm">
                <span className="text-foreground">{new Date(inv.created * 1000).toLocaleDateString()} · {money(inv.amount_paid, inv.currency)} · {inv.status}</span>
                {inv.hosted_invoice_url && (
                  <a href={inv.hosted_invoice_url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-primary underline-offset-4 hover:underline">
                    View invoice <ExternalLink className="h-3 w-3" aria-hidden="true" />
                  </a>
                )}
              </li>
            ))}
          </ul>
        )}
      </section>

      {showCancel && <CancellationModal onClose={() => setShowCancel(false)} />}
    </main>
  );
}
